import { LoadRequest, TableDataService } from '@vendasta/va-filter2-table';
import { catchError, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, share } from 'rxjs';
import { PackageService, Status } from '@vendasta/marketplace-packages';
import { FilterGroup, SearchFilterControl } from '@vendasta/va-filter2';
import { TranslateService } from '@ngx-translate/core';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../../external-dependencies/provider-tokens';
import { AppPricingService } from '@galaxy/marketplace-apps';
import { AppPrices, FieldMask } from '@vendasta/marketplace-apps';
import { PackageWithPrice } from './package-with-price';

@Injectable()
export class PackageSelectorService implements TableDataService<PackageWithPrice> {
  private readonly totalResults$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalResults$: Observable<number> = this.totalResults$$.asObservable();

  public filters: FilterGroup;

  private marketIdOverride: string;

  constructor(
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
    private readonly packageService: PackageService,
    private readonly pricingService: AppPricingService,
    private readonly translateService: TranslateService,
  ) {
    this.filters = new FilterGroup('package-selector').addToolbarSection(
      new SearchFilterControl('search', this.translateService.instant('FRONTEND.SALES_UI.SEARCH')),
    );
  }

  load(request: LoadRequest): Observable<PackageWithPrice[]> {
    const cursor = btoa((request.pageIndex * request.pageSize).toString());
    const packages$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) => {
        if (!this.marketIdOverride) {
          this.marketIdOverride = marketId;
        }
        const filterTerm = request.filters['search'] ? request.filters['search'] : '';
        return this.packageService.list(
          partnerId,
          [Status.PUBLISHED],
          this.marketIdOverride,
          request.pageSize,
          cursor,
          null,
          true,
          filterTerm,
        );
      }),
      take(1),
      map((results) => {
        if (!results || !results.packages) {
          this.totalResults$$.next(results.totalResults);
          return [];
        }
        this.totalResults$$.next(results.totalResults);
        return results.packages;
      }),
    );
    const packagePrices$ = combineLatest([this.partnerId$, this.marketId$, packages$]).pipe(
      switchMap(([partnerId, marketId, packages]) => {
        const ids = packages.map((pkg) => pkg.packageId);
        return this.pricingService.getMultiPricing(ids, new FieldMask({ paths: ['retail'] }), partnerId, marketId).pipe(
          catchError((error) => {
            console.error('Error fetching pricing data:', error);
            const appPrices: AppPrices[] = [];
            return of(appPrices);
          }),
          share(),
        );
      }),
    );

    return packagePrices$.pipe(
      take(1),
      withLatestFrom(packages$),
      map(([prices, packages]) => {
        return packages.map((pkg) => {
          for (const price of prices) {
            if (price.appId === pkg.packageId) {
              return {
                package: pkg,
                price: price.pricesForContexts['retail'],
              } as PackageWithPrice;
            }
          }

          return {
            package: pkg,
            price: null,
          };
        });
      }),
    );
  }

  setMarketId(id: string): void {
    this.marketIdOverride = id;
  }
}
